import React, { useState, useEffect } from 'react';
import { AdminService, ProgramConfig } from '../../services/adminService';
import PanelEditor from './PanelEditor';
import styles from '../../styles/admin/programsAdmin.module.css';

interface ProgramsAdminProps {
  user: any;
}

const ProgramsAdmin: React.FC<ProgramsAdminProps> = ({ user }) => {
  const [selectedProgram, setSelectedProgram] = useState<string>('saa');
  const [config, setConfig] = useState<ProgramConfig | null>(null);
  const [originalConfig, setOriginalConfig] = useState<ProgramConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [changeSummary, setChangeSummary] = useState<string>('');
  
  // Expanded sections state
  const [expandedGeneral, setExpandedGeneral] = useState(false);
  const [expandedTab1, setExpandedTab1] = useState(false);
  const [expandedTab2, setExpandedTab2] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState<Set<string>>(new Set());

  const availablePrograms = getAvailablePrograms(user?.admin_rights || []);

  useEffect(() => {
    if (availablePrograms.length > 0) {
      setSelectedProgram(availablePrograms[0]);
    }
  }, []);

  useEffect(() => {
    if (selectedProgram) {
      loadConfig();
    }
  }, [selectedProgram]);

  useEffect(() => {
    // Detect unsaved changes
    if (config && originalConfig) {
      setHasUnsavedChanges(JSON.stringify(config) !== JSON.stringify(originalConfig));
    }
  }, [config, originalConfig]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const programConfig = await AdminService.getProgramConfigAdmin(selectedProgram);
      setConfig(programConfig);
      setOriginalConfig(JSON.parse(JSON.stringify(programConfig))); // Deep clone
    } catch (err: any) {
      console.error('Error loading program config:', err);
      setError(err.message || 'Failed to load program configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const historyData = await AdminService.getProgramHistory(selectedProgram, 50);
      setHistory(historyData);
      setShowHistory(true);
    } catch (err) {
      console.error('Error loading history:', err);
    }
  };

  const handleSaveClick = () => {
    if (!config || !originalConfig) return;

    // Generate change summary
    const summary = generateChangeSummary(originalConfig, config);
    setChangeSummary(summary);
    setShowSaveConfirmation(true);
  };

  const handleSaveConfirm = async () => {
    if (!config) return;

    try {
      setSaving(true);
      setError(null);
      
      // Debug: Check if image_url is in the config before saving
      const articlesPanel = config.tab2.panels.find((p: any) => p.id === 'articles-panel');
      if (articlesPanel && articlesPanel.items && articlesPanel.items[0]) {
        console.log('Before save - Item 0 image_url:', articlesPanel.items[0].image_url);
        console.log('Before save - Item 0 link_value:', articlesPanel.items[0].link_value);
      }
      
      await AdminService.updateProgramConfig(selectedProgram, config);
      
      // Reload config to get updated metadata
      await loadConfig();
      
      // Debug: Check what came back from server after reload
      const reloadedConfig = await AdminService.getProgramConfigAdmin(selectedProgram);
      const reloadedArticlesPanel = reloadedConfig.tab2.panels.find((p: any) => p.id === 'articles-panel');
      if (reloadedArticlesPanel && reloadedArticlesPanel.items && reloadedArticlesPanel.items[0]) {
        console.log('After reload - Item 0 image_url:', reloadedArticlesPanel.items[0].image_url);
        console.log('After reload - Item 0 link_value:', reloadedArticlesPanel.items[0].link_value);
      }
      
      setHasUnsavedChanges(false);
      setShowSaveConfirmation(false);
      
      alert('Changes published successfully! The live site will update within 60 seconds.');
    } catch (err: any) {
      console.error('Error saving config:', err);
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleConfigChange = (
    updater: ProgramConfig | ((prevConfig: ProgramConfig) => ProgramConfig)
  ) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }
      const nextConfig =
        typeof updater === 'function'
          ? (updater as (prevConfig: ProgramConfig) => ProgramConfig)(prevConfig)
          : updater;
      return nextConfig;
    });
  };

  const togglePanel = (panelId: string) => {
    const newExpanded = new Set(expandedPanels);
    if (newExpanded.has(panelId)) {
      newExpanded.delete(panelId);
    } else {
      newExpanded.add(panelId);
    }
    setExpandedPanels(newExpanded);
  };

  const movePanelOrder = (panelId: string, direction: 'up' | 'down' | 'top' | 'bottom') => {
    handleConfigChange((prevConfig) => {
      const panels = [...prevConfig.tab2.panels].sort((a, b) => a.order - b.order);
      const currentIndex = panels.findIndex((p) => p.id === panelId);

      let newIndex = currentIndex;
      if (direction === 'up' && currentIndex > 0) {
        newIndex = currentIndex - 1;
      } else if (direction === 'down' && currentIndex < panels.length - 1) {
        newIndex = currentIndex + 1;
      } else if (direction === 'top') {
        newIndex = 0;
      } else if (direction === 'bottom') {
        newIndex = panels.length - 1;
      }

      if (newIndex === currentIndex) {
        return prevConfig;
      }

      const updatedPanels = [...panels];
      const [removed] = updatedPanels.splice(currentIndex, 1);
      updatedPanels.splice(newIndex, 0, removed);

      const reorderedPanels = updatedPanels.map((p, i) => ({ ...p, order: i + 1 }));

      return {
        ...prevConfig,
        tab2: {
          ...prevConfig.tab2,
          panels: reorderedPanels,
        },
      };
    });
  };

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.spinner}></div>
        <p>Loading program configuration...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorState}>
        <p>{error}</p>
        <button onClick={loadConfig} className={styles.retryButton}>
          Retry
        </button>
      </div>
    );
  }

  if (!config) {
    return (
      <div className={styles.emptyState}>
        <p>No configuration found for this program</p>
      </div>
    );
  }

  return (
    <div className={styles.programsAdmin}>
      {/* Program Selector */}
      {availablePrograms.length > 1 && (
        <div className={styles.programSelector}>
          <label htmlFor="program-select">Program:</label>
          <select
            id="program-select"
            value={selectedProgram}
            onChange={(e) => setSelectedProgram(e.target.value)}
            className={styles.select}
            disabled={hasUnsavedChanges}
          >
            {availablePrograms.map(program => (
              <option key={program} value={program}>
                {program.toUpperCase()}
              </option>
            ))}
          </select>
          {hasUnsavedChanges && (
            <span className={styles.unsavedWarning}>
              (Save or discard changes before switching programs)
            </span>
          )}
        </div>
      )}

      {/* Header with Actions */}
      <div className={styles.header}>
        <div>
          <h2>Managing: {selectedProgram.toUpperCase()} Homepage</h2>
          <p className={styles.lastUpdated}>
            Last updated: {new Date(config.updated_at).toLocaleString()} by {config.updated_by}
          </p>
        </div>
        <div className={styles.headerActions}>
          <button onClick={loadHistory} className={styles.historyButton}>
            📜 View History
          </button>
          {hasUnsavedChanges && (
            <button onClick={loadConfig} className={styles.discardButton}>
              Discard Changes
            </button>
          )}
          <button
            onClick={handleSaveClick}
            className={styles.saveButton}
            disabled={!hasUnsavedChanges || saving}
          >
            {saving ? 'Publishing...' : 'Save & Publish'}
          </button>
        </div>
      </div>

      {hasUnsavedChanges && (
        <div className={styles.unsavedBanner}>
          ⚠️ You have unsaved changes
        </div>
      )}

      {/* Configuration Editor - Accordion Layout */}
      <div className={styles.editorContainer}>
        
        {/* General Settings Panel */}
        <div className={styles.accordionPanel}>
          <div className={styles.accordionHeader} onClick={() => setExpandedGeneral(!expandedGeneral)}>
            <h3>General Settings</h3>
            <button className={`${styles.expandIcon} ${expandedGeneral ? styles.expanded : ''}`}>
              ▼
            </button>
          </div>
          {expandedGeneral && (
            <div className={styles.accordionContent}>
              <div className={styles.formGroup}>
                <label>Page Title</label>
                <input
                  type="text"
                  value={config.title}
                  onChange={(e) =>
                    handleConfigChange((prevConfig) => ({ ...prevConfig, title: e.target.value }))
                  }
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Subtitle / About Text</label>
                <textarea
                  value={config.subtitle}
                  onChange={(e) =>
                    handleConfigChange((prevConfig) => ({ ...prevConfig, subtitle: e.target.value }))
                  }
                  className={styles.textarea}
                  rows={4}
                />
              </div>
            </div>
          )}
        </div>

        {/* Tab 1 Settings Panel */}
        <div className={styles.accordionPanel}>
          <div className={styles.accordionHeader} onClick={() => setExpandedTab1(!expandedTab1)}>
            <h3>Tab One: {config.tab1.name}</h3>
            <button className={`${styles.expandIcon} ${expandedTab1 ? styles.expanded : ''}`}>
              ▼
            </button>
          </div>
          {expandedTab1 && (
            <div className={styles.accordionContent}>
              <div className={styles.formGroup}>
                <label>Tab Name</label>
                <input
                  type="text"
                  value={config.tab1.name}
                  onChange={(e) =>
                    handleConfigChange((prevConfig) => ({
                      ...prevConfig,
                      tab1: { ...prevConfig.tab1, name: e.target.value },
                    }))
                  }
                  className={styles.input}
                />
              </div>
              <p className={styles.note}>
                The 4 action buttons are configured below. (Button functionality is hardcoded in the app.)
              </p>
              
              {config.tab1.buttons.map((button: any, index: number) => (
                <div key={button.id} className={styles.buttonConfig}>
                  <h4>Button {index + 1}: {button.id}</h4>
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label>Title</label>
                      <input
                        type="text"
                        value={button.title}
                        onChange={(e) =>
                          handleConfigChange((prevConfig) => {
                            const buttons = prevConfig.tab1.buttons.map((btn: any, btnIdx: number) =>
                              btnIdx === index ? { ...btn, title: e.target.value } : btn
                            );
                            return {
                              ...prevConfig,
                              tab1: { ...prevConfig.tab1, buttons },
                            };
                          })
                        }
                        className={styles.input}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label>Icon URL</label>
                      <input
                        type="text"
                        value={button.icon_url}
                        onChange={(e) =>
                          handleConfigChange((prevConfig) => {
                            const buttons = prevConfig.tab1.buttons.map((btn: any, btnIdx: number) =>
                              btnIdx === index ? { ...btn, icon_url: e.target.value } : btn
                            );
                            return {
                              ...prevConfig,
                              tab1: { ...prevConfig.tab1, buttons },
                            };
                          })
                        }
                        className={styles.input}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                    <label>Subtext</label>
                    <textarea
                      value={button.subtext}
                      onChange={(e) =>
                        handleConfigChange((prevConfig) => {
                          const buttons = prevConfig.tab1.buttons.map((btn: any, btnIdx: number) =>
                            btnIdx === index ? { ...btn, subtext: e.target.value } : btn
                          );
                          return {
                            ...prevConfig,
                            tab1: { ...prevConfig.tab1, buttons },
                          };
                        })
                      }
                      className={styles.textarea}
                      rows={2}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tab 2 Settings Panel */}
        <div className={styles.accordionPanel}>
          <div className={styles.accordionHeader} onClick={() => setExpandedTab2(!expandedTab2)}>
            <h3>Tab Two: {config.tab2.name}</h3>
            <button className={`${styles.expandIcon} ${expandedTab2 ? styles.expanded : ''}`}>
              ▼
            </button>
          </div>
          {expandedTab2 && (
            <div className={styles.accordionContent}>
              <div className={styles.formGroup}>
                <label>Tab Name</label>
                <input
                  type="text"
                  value={config.tab2.name}
                  onChange={(e) =>
                    handleConfigChange((prevConfig) => ({
                      ...prevConfig,
                      tab2: { ...prevConfig.tab2, name: e.target.value },
                    }))
                  }
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Header Text</label>
                <textarea
                  value={config.tab2.header_text}
                  onChange={(e) =>
                    handleConfigChange((prevConfig) => ({
                      ...prevConfig,
                      tab2: { ...prevConfig.tab2, header_text: e.target.value },
                    }))
                  }
                  className={styles.textarea}
                  rows={2}
                />
              </div>

              {/* Content Panels List */}
              <div className={styles.panelsSection}>
                <div className={styles.panelsHeader}>
                  <h4>Content Panels</h4>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      const newPanelId = `panel-${Date.now()}`;
                      handleConfigChange((prevConfig) => {
                        const newPanel = {
                          id: newPanelId,
                          title: 'New Panel',
                          subtext: '',
                          format: 'list_links' as const,
                          order: prevConfig.tab2.panels.length + 1,
                          items: [],
                        };
                        return {
                          ...prevConfig,
                          tab2: {
                            ...prevConfig.tab2,
                            panels: [...prevConfig.tab2.panels, newPanel],
                          },
                        };
                      });
                      togglePanel(newPanelId);
                    }} 
                    className={styles.addPanelButton}
                    disabled={config.tab2.panels.length >= 9}
                  >
                    + Add Panel
                  </button>
                </div>

                {/* Content Panels - Each with expand/contract */}
                {[...config.tab2.panels].sort((a, b) => a.order - b.order).map((panel, index) => (
                  <div key={panel.id} className={styles.contentPanel}>
                    <div className={styles.contentPanelHeader} onClick={() => togglePanel(panel.id)}>
                      <div className={styles.panelInfo}>
                        <strong>{panel.title}</strong>
                        <span className={styles.panelMeta}>
                          ({panel.format.replace(/_/g, ' ')}) • {panel.items.length} items
                        </span>
                      </div>
                      <div className={styles.panelActions} onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => movePanelOrder(panel.id, 'top')}
                          disabled={index === 0}
                          className={styles.moveButton}
                          title="Move to top"
                        >
                          ⏫
                        </button>
                        <button
                          onClick={() => movePanelOrder(panel.id, 'up')}
                          disabled={index === 0}
                          className={styles.moveButton}
                          title="Move up one"
                        >
                          ⬆️
                        </button>
                        <button
                          onClick={() => movePanelOrder(panel.id, 'down')}
                          disabled={index === config.tab2.panels.length - 1}
                          className={styles.moveButton}
                          title="Move down one"
                        >
                          ⬇️
                        </button>
                        <button
                          onClick={() => movePanelOrder(panel.id, 'bottom')}
                          disabled={index === config.tab2.panels.length - 1}
                          className={styles.moveButton}
                          title="Move to bottom"
                        >
                          ⏬
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Remove "${panel.title}" panel and all its content?`)) {
                              handleConfigChange((prevConfig) => {
                                const filteredPanels = prevConfig.tab2.panels
                                  .filter((p: any) => p.id !== panel.id)
                                  .map((p: any, i: number) => ({ ...p, order: i + 1 }));
                                return {
                                  ...prevConfig,
                                  tab2: {
                                    ...prevConfig.tab2,
                                    panels: filteredPanels,
                                  },
                                };
                              });
                            }
                          }}
                          className={styles.removeButton}
                          title="Remove panel"
                        >
                          🗑️
                        </button>
                        <button className={`${styles.expandIcon} ${expandedPanels.has(panel.id) ? styles.expanded : ''}`}>
                          ▼
                        </button>
                      </div>
                    </div>

                    {expandedPanels.has(panel.id) && (
                      <div className={styles.contentPanelBody}>
                        <PanelEditor
                          panels={[panel]}
                          programId={selectedProgram}
                          onChange={(updatedPanels) => {
                            const targetPanelId = panel.id;
                            handleConfigChange((prevConfig) => {
                              const updatedTab2Panels = prevConfig.tab2.panels.map((p: any) =>
                                p.id === targetPanelId ? updatedPanels[0] : p
                              );
                              return {
                                ...prevConfig,
                                tab2: {
                                  ...prevConfig.tab2,
                                  panels: updatedTab2Panels,
                                },
                              };
                            });
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}

                {config.tab2.panels.length === 0 && (
                  <div className={styles.emptyPanels}>
                    No content panels yet. Click "Add Panel" to create one.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save Confirmation Dialog */}
      {showSaveConfirmation && (
        <div className={styles.dialogOverlay} onClick={() => !saving && setShowSaveConfirmation(false)}>
          <div className={styles.dialogBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.dialogHeader}>
              <h3>Confirm Publish</h3>
              <button onClick={() => setShowSaveConfirmation(false)} className={styles.dialogClose} disabled={saving}>
                ×
              </button>
            </div>
            <div className={styles.dialogContent}>
              <p><strong>You are about to publish these changes:</strong></p>
              <div className={styles.changeSummary}>{changeSummary}</div>
              <p>This will update the live site immediately.</p>
            </div>
            <div className={styles.dialogActions}>
              <button onClick={() => setShowSaveConfirmation(false)} className={styles.cancelButton} disabled={saving}>
                Cancel
              </button>
              <button onClick={handleSaveConfirm} className={styles.confirmButton} disabled={saving}>
                {saving ? 'Publishing...' : 'Publish Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Dialog */}
      {showHistory && (
        <div className={styles.dialogOverlay} onClick={() => setShowHistory(false)}>
          <div className={styles.dialogBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.dialogHeader}>
              <h3>Change History</h3>
              <button onClick={() => setShowHistory(false)} className={styles.dialogClose}>
                ×
              </button>
            </div>
            <div className={styles.dialogContent}>
              {history.length === 0 ? (
                <p>No change history available</p>
              ) : (
                <div className={styles.historyList}>
                  {history.map((entry, index) => (
                    <div key={index} className={styles.historyEntry}>
                      <div className={styles.historyDate}>
                        {new Date(entry.timestamp).toLocaleString()}
                      </div>
                      <div className={styles.historyAdmin}>
                        By: {entry.admin_email}
                      </div>
                      <div className={styles.historySummary}>
                        {entry.change_summary}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function getAvailablePrograms(adminRights: string[]): string[] {
  if (!adminRights || adminRights.length === 0) return [];
  if (adminRights.includes('All')) return ['saa', 'aa', 'na'];
  
  const programs: string[] = [];
  if (adminRights.includes('SAA')) programs.push('saa');
  if (adminRights.includes('AA')) programs.push('aa');
  if (adminRights.includes('NA')) programs.push('na');
  
  return programs;
}

function generateChangeSummary(oldConfig: any, newConfig: any): string {
  const changes: string[] = [];

  // Title/subtitle changes
  if (oldConfig.title !== newConfig.title) {
    changes.push(`Changed title to "${newConfig.title}"`);
  }
  if (oldConfig.subtitle !== newConfig.subtitle) {
    changes.push('Updated subtitle');
  }

  // Tab changes
  if (oldConfig.tab2?.name !== newConfig.tab2?.name) {
    changes.push(`Changed Tab 2 name to "${newConfig.tab2.name}"`);
  }

  // Panel changes
  const oldPanels = oldConfig.tab2?.panels || [];
  const newPanels = newConfig.tab2?.panels || [];

  if (oldPanels.length !== newPanels.length) {
    const diff = newPanels.length - oldPanels.length;
    if (diff > 0) {
      changes.push(`Added ${diff} panel(s)`);
    } else {
      changes.push(`Removed ${Math.abs(diff)} panel(s)`);
    }
  }

  // Check for item count changes in panels
  oldPanels.forEach((oldPanel: any, index: number) => {
    const newPanel = newPanels.find((p: any) => p.id === oldPanel.id);
    if (newPanel) {
      const oldItems = oldPanel.items?.length || 0;
      const newItems = newPanel.items?.length || 0;
      if (oldItems !== newItems) {
        const diff = newItems - oldItems;
        if (diff > 0) {
          changes.push(`Added ${diff} item(s) to ${newPanel.title}`);
        } else {
          changes.push(`Removed ${Math.abs(diff)} item(s) from ${newPanel.title}`);
        }
      }
    }
  });

  return changes.length > 0 ? changes.join(', ') : 'Minor updates';
}

export default ProgramsAdmin;

